import { useCallback, useSyncExternalStore } from "react";

/*
 * Light / dark theme runtime.
 *
 * The preference is shared with the marketing site: a `gf_theme=light|dark`
 * cookie on `.magicallylinked.com` (so www and app agree), mirrored to localStorage
 * `gf-theme` for hosts where that cookie can't be set (localhost, the
 * extension popup). No stored choice means "system": follow the device.
 *
 * The resolved theme lives on <html data-theme>. An inline script in each
 * app's index.html applies it before first paint; this module keeps it in sync
 * afterwards (toggle, device changes, another tab or the marketing site).
 */

export type Theme = "light" | "dark";
export type ThemePref = Theme | "system";

const COOKIE = "gf_theme";
const STORAGE_KEY = "gf-theme";
const SHARED_DOMAIN = "magicallylinked.com";
const ONE_YEAR = 60 * 60 * 24 * 365;
const META_COLOR: Record<Theme, string> = { dark: "#000000", light: "#FFFFFF" };

const isTheme = (v: unknown): v is Theme => v === "light" || v === "dark";

function cookieDomain(): string | null {
  const host = typeof location === "undefined" ? "" : location.hostname;
  return host === SHARED_DOMAIN || host.endsWith(`.${SHARED_DOMAIN}`) ? `.${SHARED_DOMAIN}` : null;
}

export function readThemePref(): ThemePref {
  try {
    const m = document.cookie.match(/(?:^|;\s*)gf_theme=(light|dark)\b/);
    if (m && isTheme(m[1])) return m[1];
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // Storage blocked (privacy mode, sandboxed frame): fall through to the device setting.
  }
  return "system";
}

const systemQuery = () =>
  typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: light)") : null;

export function systemTheme(): Theme {
  return systemQuery()?.matches ? "light" : "dark";
}

export function resolveTheme(pref: ThemePref = readThemePref()): Theme {
  return pref === "system" ? systemTheme() : pref;
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (root.dataset.theme !== theme) root.dataset.theme = theme;
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", META_COLOR[theme]);
}

function persist(pref: ThemePref): void {
  const domain = cookieDomain();
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  const base = `; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}${secure}`;
  try {
    if (pref === "system") {
      document.cookie = `${COOKIE}=; Max-Age=0${base}`;
      localStorage.removeItem(STORAGE_KEY);
    } else {
      document.cookie = `${COOKIE}=${pref}; Max-Age=${ONE_YEAR}${base}`;
      localStorage.setItem(STORAGE_KEY, pref);
    }
  } catch {
    // Not persisted; the choice still applies for this page view.
  }
}

// ── Store (one per document) ────────────────────────────────────────────────

interface Snapshot {
  pref: ThemePref;
  theme: Theme;
}

const listeners = new Set<() => void>();
let current: Snapshot | null = null;
let wired = false;

function snapshot(): Snapshot {
  if (!current) {
    const pref = readThemePref();
    current = { pref, theme: resolveTheme(pref) };
  }
  return current;
}

function refresh(pref: ThemePref = readThemePref()): void {
  const theme = resolveTheme(pref);
  applyTheme(theme);
  if (current && current.pref === pref && current.theme === theme) return;
  current = { pref, theme };
  listeners.forEach((l) => l());
}

function wire(): void {
  if (wired || typeof window === "undefined") return;
  wired = true;
  systemQuery()?.addEventListener("change", () => refresh());
  // Another tab changed it, or the user came back from the marketing site.
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY || e.key === null) refresh();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });
}

function subscribe(listener: () => void): () => void {
  wire();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Set (and remember) the theme preference. `system` forgets the choice and follows the device. */
export function setThemePref(pref: ThemePref): void {
  persist(pref);
  refresh(pref);
}

/** Current preference + resolved theme, re-rendering on any change. */
export function useTheme(): { pref: ThemePref; theme: Theme; setPref: (pref: ThemePref) => void } {
  const snap = useSyncExternalStore(subscribe, snapshot, snapshot);
  const setPref = useCallback((pref: ThemePref) => setThemePref(pref), []);
  return { pref: snap.pref, theme: snap.theme, setPref };
}
